import React, { useState, useEffect } from "react";
import cookie from "react-cookies";

import { ApiCallResult, useAPI } from "services/Api";
import Input from "input/Input";
import Page from "pages/Page";
import { navigate } from "utility/NavigationService";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";

const UserSettings = ({ componentName = "[UserSettings]" }) => {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [lastName, setLastName] = useState("");
    const [reset, setReset] = useState(false);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const user = cookie.load("user") ? cookie.load("user") : null;

    // ---=== Utilities ===--- //

    const changePassword = () => {
        void navigate("/password/change/");
    };

    const handleSubmit = (event) => {
        setIsLoading(true);
        event.preventDefault();

        void api_UpdateData(
            JSON.stringify({
                first_name: firstName,
                last_name: lastName
            })
        );
    };

    // ---=== KarunaCLOUD ===--- //

    const api_GetData = async () => {
        const result: ApiCallResult = await API.api_Get(
            componentName,
            `/user/`,
            notificationManager,
            (response) => {
                setFirstName(response.data.first_name);
                setLastName(response.data.last_name);
                setEmail(response.data.email);
                setIsLoading(false);
            }
        );

        setIsLoading(!(result.wasSuccessful && result.wasCallable));
    };

    const api_UpdateData = async (data) => {
        const isSecure = window.location.protocol === "https:";

        const result: ApiCallResult = await API.api_Patch(
            componentName,
            `/user/`,
            data,
            notificationManager,
            (_response) => {
                user.first_name = firstName;
                user.last_name = lastName;
                cookie.save(
                    "user",
                    user,
                    {
                        maxAge: 3600, // 1 hour
                        path: "/",
                        sameSite: isSecure ? "none" : false,
                        secure: isSecure
                    }
                );

                setReset(true);
                notificationManager.addSuccess("User profile updated successfully.", { duration: 10 });
            },
            (error) => {
                notificationManager.addError(error.response?.data);
            }
        );

        setIsLoading(!(result.wasSuccessful && result.wasCallable));
    };

    // ---=== useEffects ===--- //

    useEffect(() => {
        void api_GetData();
    }, []);

    useEffect(() => {
        if (reset) {
            window.location.reload();
        }
    }, [reset]);

    // ---=== Render ===--- //

    const getModularHeaderConfig = () => {
        return {
            trail: breadCrumbLinks(),
            body: {
                subheader: "Edit Profile",
                name: user ? `${user.first_name} ${user.last_name}` : "",
                buttons: [
                    {
                        title: "change password",
                        click: changePassword,
                        img: "/icons/passcode-lock.png"
                    }
                ]
            }
        };
    };

    const breadCrumbLinks = () => {
        return [
            { label: user ? `${user.first_name} ${user.last_name}` : "User Profile", url: `/user` }
        ];
    };

    return (
        <React.Fragment>
            <Page
                componentName={componentName}
                headerConfig={getModularHeaderConfig()}
            >
                {
                    isLoading
                        ? (
                            renderLoader()
                        )
                        : (
                            <form className="relative" onSubmit={handleSubmit}>
                                <div className="w-full p-6 flex-col justify-end items-start gap-5 inline-flex">
                                    <div className="w-1/3 flex-col justify-start items-start gap-8 flex">
                                        <div className="self-stretch h-[164px] flex-col justify-start items-start gap-6 flex">
                                            <div className="grow shrink self-stretch flex gap-8">
                                                <div className="grow shrink self-stretch h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                    <div className="self-stretch grow shrink h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                        <div className="text-slate-700 text-sm font-medium leading-tight">First Name</div>
                                                        <Input
                                                            className="text-gray-500 text-base font-normal self-auto px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex"
                                                            name="First Name"
                                                            required
                                                            type="text"
                                                            value={firstName}
                                                            // Callbacks
                                                            onChange={setFirstName}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grow shrink self-stretch h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                    <div className="grow shrink self-stretch h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                        <div className="text-slate-700 flex text-sm font-medium leading-tight">Last Name</div>
                                                        <Input
                                                            className="text-gray-500  text-base font-normal self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex"
                                                            name="Last Name"
                                                            required
                                                            type="text"
                                                            value={lastName}
                                                            // Callbacks
                                                            onChange={setLastName}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grow shrink self-stretch h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                <div className="self-stretch h-[70px] flex-col justify-start items-start gap-1.5 flex">
                                                    <div className="text-gray-700 text-sm font-medium leading-tight">Email</div>
                                                    <Input
                                                        className="text-gray-500 text-base font-normal self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex"
                                                        name="Email (optional)"
                                                        readonly={true}
                                                        type="text"
                                                        validations={[{ type: "email", value: email }]}
                                                        value={email}
                                                        // Callbacks
                                                        onChange={setEmail}
                                                    />
                                                    <p className=" text-gray-600 whitespace-nowrap text-sm font-normal ">Contact a Karuna admin to change your account’s email address.</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="self-stretch h-12 flex-col justify-start items-start gap-4 mt-5 flex">
                                            <button className="self-stretch px-[18px] py-3 bg-brand-600 hover:bg-brand-700 rounded-lg shadow border border-brand-600 justify-center items-center gap-1.5 inline-flex cursor-pointer">
                                                <div className="w-5 h-5 relative"/>
                                                <div className="px-0.5 justify-center items-center flex">
                                                    <div className="text-white text-base font-semibold leading-normal">Save User</div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )
                }
            </Page>
        </React.Fragment>
    );
};

export default UserSettings;
